import { computed, inject, Injectable, signal } from '@angular/core';
import { GameService } from '@core/game/game.service';
import { LpProjectSettingsService } from '@core/lp/lp-project-settings.service';
import { SAVE_SERVICE } from '@core/saves/saves.interface';
import { Wk8DemoService } from '@core/wk8/wk8-demo.service';
import { AdaptivityService } from '@features/wk8/adaptivity.service';
import { Wk8AudioService } from '@features/wk8/core/wk8-audio/wk8-audio.service';
import { Wk8WordService } from '@features/wk8/core/wk8-words/wk8-word-service.service';
import { waitForMillis } from '@shared/delay.utils';
import { LpWk8MechanicDetails, LpWk8Mechanics } from '@typings/lp/generated';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { Uid } from '@typings/id';

export type TMechanic =
  | 'lees_woord'
  | 'lees_flitsen'
  | 'lees_flitsenMutaties'
  | 'lees_zin'
  | 'sorteer_woorden'
  | 'wissel_klanken'
  | 'woordpad';
export type TMechanicWithTutorial = Exclude<keyof LpWk8Mechanics, 'dataType' | 'uid'>;

@Injectable({
  providedIn: 'root',
})
export class Wk8TutorService {
  readonly #lpProjectSettingsService = inject(LpProjectSettingsService);
  readonly #wk8AudioService = inject(Wk8AudioService);
  readonly #gameService = inject(GameService);
  readonly #wk8DemoService = inject(Wk8DemoService);
  readonly #adaptivityService = inject(AdaptivityService);
  readonly #wk8WordService = inject(Wk8WordService);
  readonly #saveService = inject(SAVE_SERVICE);

  private registeredPanels = 0;

  private nextClickedSource = new Subject<void>();
  nextClicked$ = this.nextClickedSource.asObservable();

  private finishedSource = new Subject<boolean>();
  private finishedTime = 0;
  private finished$ = this.finishedSource.asObservable();

  // Indicates if at least one caller is waiting for "next" to be clicked.
  private waitingForConfirmSource = new BehaviorSubject<boolean>(false);
  waitingForConfirm = this.waitingForConfirmSource.asObservable();

  // Holds the message to display on the next button.
  private confirmButtonMessageSource = new BehaviorSubject<string>('Next');
  confirmButtonMessage$ = this.confirmButtonMessageSource.asObservable();

  // correctness
  public isIncorrect = signal<boolean>(false);

  // current ui contents
  public mechanicInfo = signal<LpWk8MechanicDetails | undefined>(undefined);

  // read check
  public wordToCheck = signal<string | undefined>(undefined);
  public isChecking = computed(() => this.wordToCheck() !== undefined);
  #shouldHideBackground = signal(false);
  public shouldHideBackground = computed(() => this.#shouldHideBackground());

  // icon
  public icon = signal<((icon: string | undefined, name: string | undefined) => Promise<void>)[]>([]);

  public isPlayingTutorial = signal(false);

  // action
  private actionFinishedSource = new Subject<void>();
  private actionFinished$ = this.actionFinishedSource.asObservable();
  public action = signal<string | undefined>(undefined);
  public isRequiringAction = computed(() => this.action() !== undefined);

  // choice
  private choiceFinishedSource = new Subject<string>();
  private choiceFinished$ = this.choiceFinishedSource.asObservable();
  public choices = signal<string[]>([]);
  public isRequiringChoice = computed(() => this.choices().length > 0);

  // tutorials
  private seenAudioInstructions = new Set<Uid>();

  constructor() {
    this.#saveService.load().then((save) => {
      save.seenMechanicIntros.forEach((uid) => this.seenAudioInstructions.add(uid as Uid));
    });
  }

  public registerPanel() {
    this.registeredPanels++;
  }

  public unregisterPanel() {
    this.registeredPanels--;
  }

  notifyNextClicked(): void {
    this.nextClickedSource.next();
    // Reset the waiting flag and next button message once the next button is pressed.
    this.waitingForConfirmSource.next(false);
    this.confirmButtonMessageSource.next('Next');
  }

  /**
   * Call this function when waiting for the tutor to press next.
   * The caller can specify a custom button message that will be displayed.
   * Returns a promise that resolves when the next button is clicked.
   */
  waitForConfirm(buttonMessage: string = 'Next'): Promise<void> {
    this.waitingForConfirmSource.next(true);
    this.confirmButtonMessageSource.next(buttonMessage);
    return firstValueFrom(this.nextClicked$);
  }

  public async showMechanic(id: TMechanicWithTutorial): Promise<void> {
    const settings = await this.#lpProjectSettingsService.getSettings();
    const mechanic = settings.woordkr8.mechanics[id] as LpWk8MechanicDetails;

    if (mechanic.showIcon && mechanic.icon?.url && mechanic.iconTitle) {
      await this.showIcon(mechanic.icon?.url, mechanic.iconTitle);
    } else {
      this.hideIcon();
    }

    this.mechanicInfo.set({
      ...mechanic,
      title: mechanic.title ? (mechanic.title.length === 0 ? undefined : mechanic.title) : undefined,
      description: mechanic.description ? (mechanic.description.length === 0 ? undefined : mechanic.description) : undefined,
    });

    // play simple audio tutorial
    const instruction = mechanic.instructionAudio;
    if (instruction && !this.seenAudioInstructions.has(instruction.uid as Uid) && !this.#wk8DemoService.hasTutorialsDisabled()) {
      await waitForMillis(500); // NOTE: wait for transition
      await this.playMechanicInstructionAudio(mechanic);
    }
  }

  public async showIcon(icon: string, title: string) {
    await Promise.all(this.icon().map(async (fn) => await fn(icon, title)));
  }

  public async hideIcon() {
    await Promise.all(this.icon().map(async (fn) => await fn(undefined, undefined)));
  }

  public async playMechanicInstructionAudio(mechanic: LpWk8MechanicDetails): Promise<void> {
    if (!mechanic.instructionAudio) return;
    this.isPlayingTutorial.set(true);

    // play audio tutorial
    await this.#wk8AudioService.play(mechanic.instructionAudio.url).waitForEnd();
    this.seenAudioInstructions.add(mechanic.instructionAudio.uid as Uid);

    this.isPlayingTutorial.set(false);

    // save to cache
    const uid = mechanic.instructionAudio.uid;
    this.#saveService.load().then((save) => {
      save.seenMechanicIntros.push(uid);
      this.#saveService.save(save);
    });
  }

  public async playTutorial(mechanic: LpWk8MechanicDetails): Promise<void> {
    if (!mechanic.tutorial) return;

    this.isPlayingTutorial.set(true);
    await this.#gameService.playStory(mechanic.tutorial, true);
    this.isPlayingTutorial.set(false);
  }

  public async waitForReadCheck(word: string, skipCorrectUpdate = false, shouldHideBackground = false): Promise<boolean> {
    this.#shouldHideBackground.set(shouldHideBackground);
    console.log('Checking word:', word);
    this.wordToCheck.set(word);
    if (Date.now() > this.finishedTime + 250) {
      this.#wk8AudioService.playReadCheckStart();
    }

    if (this.registeredPanels === 0) {
      await waitForMillis(1000);
      this.markSuccess();
      return true;
    }
    const correct = await firstValueFrom(this.finished$);
    if (skipCorrectUpdate) return correct;

    const wordData = this.#wk8WordService.tryFindWord(word, 'ALL');
    if (wordData) {
      this.#adaptivityService.adaptiveUpdate(wordData, correct);
    } else {
      console.warn(`Word '${word}' not found in the list of valid words for read check.`);
    }

    return correct;
  }

  public async waitForAction(text: string): Promise<void> {
    this.action.set(text);

    if (this.registeredPanels === 0) {
      await waitForMillis(1000);
      this.markActionFinished();
      return;
    }

    return await firstValueFrom(this.actionFinished$);
  }

  public async waitForChoice<T extends string>(...choices: T[]): Promise<T> {
    this.choices.set(choices);

    return await firstValueFrom(this.choiceFinished$ as Observable<T>);
  }

  public async markSuccess() {
    this.wordToCheck.set(undefined);
    this.#wk8AudioService.playReadCheckCorrect();
    this.finishedSource.next(true);
    this.finishedTime = Date.now();
  }

  public async markPractice() {
    this.wordToCheck.set(undefined);
    this.#wk8AudioService.playReadCheckIncorrect();
    this.finishedSource.next(false);
    this.finishedTime = Date.now();
  }

  public markActionFinished() {
    this.actionFinishedSource.next();
    this.action.set(undefined);
  }

  public markChoice(choice: string) {
    this.choiceFinishedSource.next(choice);
    this.choices.set([]);
  }

  public clear() {
    this.wordToCheck.set(undefined);
    this.mechanicInfo.set(undefined);
  }

  public async incorrect() {
    this.#wk8AudioService.playActivityIncorrect();
    this.isIncorrect.set(true);
    await waitForMillis(1000); // wait for animation overlay
    this.isIncorrect.set(false);
  }
}
